import type { Fund } from '@/modules/fund/domain/fundraising';
import type {
  FundFilingStatus,
  FundFormValues,
  FundraisingStatus,
  InvestmentFundRecord,
} from '@/modules/fund/types/fund';

/** 派生募集状态（正交维度）：未开始、募集进行中、首关完成、终关完成、募集完成。 */
export const resolveFundraisingStatus = (
  record: Partial<InvestmentFundRecord>,
): FundraisingStatus => {
  if (record.fundraisingStatus) return record.fundraisingStatus;
  if (record.finalCloseAt) return '终关完成';
  if (record.firstCloseAt) return '首关完成';
  const phase = record.phase;
  if (record.raisingStarted || record.raisingStartedAt || phase === '募集期')
    return '募集进行中';
  if (
    ['投资期', '退出期', '延长期', '清算中', '清算完成'].includes(phase ?? '')
  )
    return '募集完成';
  return '未开始';
};

/** 派生备案状态（正交维度）：未准备、待提交、办理中、待补充材料、已备案。 */
export const resolveFilingStatus = (
  record: Partial<InvestmentFundRecord>,
): FundFilingStatus => {
  const declared = record.legalFormDetails?.basicProfile?.filingStatus;
  if (declared === '已备案') return '已备案';
  if (declared === '未备案') return '未准备';
  if (record.filingStatus) return record.filingStatus;
  if (record.registrationNumber) return '已备案';
  return '未准备';
};

const toOptionalNumber = (
  value: string | number | null | undefined,
): number | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** 后端基金记录 → 领域 Fund（id 用后端 uuid；status 映射后端 phase，枚举值两端一致）。 */
export const toDomainFund = (record: InvestmentFundRecord): Fund => ({
  legalFormDetails: record.legalFormDetails ?? null,
  id: record.uuid,
  legalEntityUuid: record.legalEntityUuid,
  legalName: record.legalName,
  legalEntity: record.legalEntity,
  code: record.code ?? undefined,
  generalPartnerUuid: record.generalPartnerUuid ?? null,
  currency: record.currency,
  name: record.name,
  shortName: record.shortName ?? record.name,
  // inv_fund.type 已降级为只读历史列（服务端永不写入）：保持 null/undefined 原样，
  // 绝不能折叠成 '—'——破折号是 truthy，会让所有 `type || fundType` 兜底与筛选失效。
  // 展示与筛选统一走 types/fund.ts 的 resolveFundTypeLabel / resolveFundTypeValue。
  type: record.type ?? null,
  registrationType: record.registrationType ?? undefined,
  organizationForm: record.organizationForm ?? undefined,
  manager: record.manager ?? '',
  riskManager: record.riskManager ?? null,
  owner: record.ownerName ?? '',
  ownerUuid: record.ownerUuid ?? null,
  target: Number(record.targetAmount ?? 0),
  status: record.phase,
  phase: record.phase,
  establishedAt: record.establishedAt ?? '',
  updatedAt: record.updatedAt ?? '',
  duration: record.durationYears ? `${record.durationYears}年` : '',
  region: record.region ?? '',
  regionUuid: record.regionUuid,
  strategy: record.strategy ?? '',
  industries: record.industries ?? [],
  minimum: Number(record.minimumAmount ?? 0),
  firstCloseTarget: Number(record.firstCloseTarget ?? 0),
  raisingStarted: record.raisingStarted ?? false,
  hasActiveTerms: record.hasActiveTerms ?? false,
  stateAssetFilingRequired: record.stateAssetFilingRequired === true,
  basisRevision: record.basisRevision ?? 1,
  phaseBasis: record.phaseBasis,
  raisingStartedAt: record.raisingStartedAt ?? null,
  firstCloseAt: record.firstCloseAt ?? '',
  finalCloseAt: record.finalCloseAt ?? '',
  gpCommitmentAmount:
    record.gpCommitmentAmount == null
      ? undefined
      : Number(record.gpCommitmentAmount),
  ourLpCommitmentAmount:
    record.ourLpCommitmentAmount == null
      ? undefined
      : Number(record.ourLpCommitmentAmount),
  finalCloseTarget:
    record.finalCloseTarget == null
      ? undefined
      : Number(record.finalCloseTarget),
  investmentRestrictions: record.investmentRestrictions ?? undefined,
  controlType: record.controlType ?? '自管',
  fundraisingStatus: resolveFundraisingStatus(record),
  filingStatus: resolveFilingStatus(record),
  ourShareRatio:
    record.ourShareRatio == null || record.ourShareRatio === ''
      ? undefined
      : Number(record.ourShareRatio),
  managementMode: record.managementMode ?? null,
  managementBasis: record.managementBasis ?? null,
  classificationStatus: record.classificationStatus ?? 'pending_review',
  internalManagerOrgUuid: record.internalManagerOrgUuid ?? null,
  managementClassificationNote: record.managementClassificationNote ?? null,
  totalCommitmentAmount: record.totalCommitmentAmount ?? null,
  totalCommitmentSource: record.totalCommitmentSource ?? null,
  totalCommitmentAsOf: record.totalCommitmentAsOf ?? null,
  totalCommitmentAttachmentUuid: record.totalCommitmentAttachmentUuid ?? null,
  ourInvestorOrgUuids: record.ourInvestorOrgUuids ?? [],
  isAbsoluteControl: record.isAbsoluteControl,
  controlRemark: record.controlRemark ?? undefined,
  fundKind: record.fundKind ?? undefined,
  registrationStatus: record.registrationStatus ?? null,
  fundType: record.fundType ?? null,
  fundLevel: record.fundLevel ?? null,
  legalRepresentative: record.legalRepresentative ?? null,
  unifiedSocialCreditCode: record.unifiedSocialCreditCode ?? null,
  businessRegistrationDate: record.businessRegistrationDate ?? null,
  businessRegistrationExpiryDate: record.businessRegistrationExpiryDate ?? null,
  organizationCode: record.organizationCode ?? null,
  registeredAddress: record.registeredAddress ?? null,
  isSimplifiedManagement: record.isSimplifiedManagement ?? null,
  custodianUuid: record.custodianUuid ?? null,
  fundManagerUuid: record.fundManagerUuid ?? null,
  managerUnifiedSocialCreditCode: record.managerUnifiedSocialCreditCode ?? null,
  managementEntity: record.managementEntity ?? null,
  partnershipEntity: record.partnershipEntity ?? null,
  partnershipOrganizationUuid: record.partnershipOrganizationUuid ?? null,
  fundraisingStartAt: record.fundraisingStartAt ?? null,
  fundraisingEndAt: record.fundraisingEndAt ?? null,
  investmentPeriodEndAt: record.investmentPeriodEndAt ?? null,
  exitPeriodEndAt: record.exitPeriodEndAt ?? null,
  strategyType: record.strategyType ?? null,
  registrationNumber: record.registrationNumber ?? null,
  registrationDate: record.registrationDate ?? null,
  investmentSubjectUuid: record.investmentSubjectUuid ?? null,
  investmentSubjectKind: record.investmentSubjectKind ?? null,
  investmentSubjectName: record.investmentSubjectName ?? null,
  managementPlatformUuid: record.managementPlatformUuid ?? null,
  managementPlatformName: record.managementPlatformName ?? null,
  ownerPlatformUuid: record.ownerPlatformUuid ?? null,
  ownerPlatformName: record.ownerPlatformName ?? null,
  sourceFundUuid: record.sourceFundUuid ?? null,
  // 经济条款必须回填原值：否则编辑任意字段都会用弹窗默认 2/20/8 覆盖原费率。
  managementFeeRate: toOptionalNumber(record.managementFeeRate),
  carryRate: toOptionalNumber(record.carryRate),
  hurdleRate: toOptionalNumber(record.hurdleRate),
  distributionWaterfall: record.distributionWaterfall ?? null,
  distributionMode: record.distributionMode ?? null,
  distributionBasis: record.distributionBasis ?? null,
  distributionFrequency: record.distributionFrequency ?? null,
  requestReturnRatio: toOptionalNumber(record.requestReturnRatio),
  requestReturnAmount: toOptionalNumber(record.requestReturnAmount),
  returnRequirement: record.returnRequirement ?? null,
  returnCompletion: record.returnCompletion ?? null,
  investmentPeriodYears: record.investmentPeriodYears ?? null,
  exitPeriodYears: record.exitPeriodYears ?? null,
  isRollOver: record.isRollOver ?? null,
  isPaybackOver: record.isPaybackOver ?? null,
  investmentPeriodStartAt: record.investmentPeriodStartAt ?? null,
  investmentPeriodExtendEndAt: record.investmentPeriodExtendEndAt ?? null,
  durationDesc: record.durationDesc ?? null,
  investmentManagementFeeBasis: record.investmentManagementFeeBasis ?? null,
  exitManagementFeeBasis: record.exitManagementFeeBasis ?? null,
  exitManagementFeeRate: toOptionalNumber(record.exitManagementFeeRate),
  extensionManagementFeeType: record.extensionManagementFeeType ?? null,
  managementFeeDesc: record.managementFeeDesc ?? null,
  incomeDistributionDesc: record.incomeDistributionDesc ?? null,
  managerExcessRatio: toOptionalNumber(record.managerExcessRatio),
  lpExcessRatio: toOptionalNumber(record.lpExcessRatio),
  custodyFeeDesc: record.custodyFeeDesc ?? null,
  executionServiceFee: record.executionServiceFee ?? null,
  businessScope: record.businessScope ?? null,
  custodyAgreementRequired: record.custodyAgreementRequired ?? null,
  delegatedRepresentative: record.delegatedRepresentative ?? null,
  companyId: record.companyId ?? null,
  companyName: record.companyName ?? null,
  deptId: record.deptId ?? null,
  departmentName: record.departmentName ?? null,
  orgGroupId: record.orgGroupId ?? null,
  groupName: record.groupName ?? null,
});

const parseDurationYears = (duration: string): number | undefined => {
  const match = duration.match(/\d+/);
  return match ? Number(match[0]) : undefined;
};

/** 领域 Fund → 编辑表单初始值。 */
export const fundToFormValues = (fund: Fund): FundFormValues => ({
  legalFormDetails: fund.legalFormDetails ?? null,
  legalEntityUuid: fund.legalEntityUuid,
  generalPartnerUuid: fund.generalPartnerUuid ?? null,
  name: fund.name,
  code: fund.code || undefined,
  shortName: fund.shortName,
  // type 不再提交（FundFormModal 提交前会删除），此处只回填原值供表单读取旧档案。
  type: fund.type ?? undefined,
  phase: fund.phase,
  stateAssetFilingRequired: fund.stateAssetFilingRequired === true,
  registrationType: fund.registrationType || undefined,
  organizationForm: fund.organizationForm || '合伙型',
  manager: fund.manager || undefined,
  riskManager: fund.riskManager ?? undefined,
  ownerName: fund.owner || undefined,
  ownerUuid: fund.ownerUuid ?? undefined,
  targetAmount: fund.target ?? undefined,
  minimumAmount: fund.minimum || undefined,
  firstCloseTarget: fund.firstCloseTarget || undefined,
  gpCommitmentAmount: fund.gpCommitmentAmount,
  ourLpCommitmentAmount: fund.ourLpCommitmentAmount,
  finalCloseTarget: fund.finalCloseTarget,
  investmentRestrictions: fund.investmentRestrictions || undefined,
  durationYears: parseDurationYears(fund.duration),
  region: fund.region || undefined,
  regionUuid: fund.regionUuid,
  strategy: fund.strategy || undefined,
  industries: fund.industries,
  establishedAt: fund.establishedAt || undefined,
  firstCloseAt: fund.firstCloseAt || undefined,
  finalCloseAt: fund.finalCloseAt || undefined,
  controlType: fund.controlType ?? '自管',
  ourShareRatio: fund.ourShareRatio,
  investmentDecisionDate: fund.investmentDecisionDate ?? undefined,
  investmentDecisionNo: fund.investmentDecisionNo ?? undefined,
  decisionConclusion: fund.decisionConclusion ?? undefined,
  lpaSignDate: fund.lpaSignDate ?? undefined,
  gpContactName: fund.gpContactName ?? undefined,
  advisorySeat: fund.advisorySeat ?? undefined,
  keyPersonClause: fund.keyPersonClause ?? undefined,
  managementMode: fund.managementMode,
  managementBasis: fund.managementBasis,
  classificationStatus: fund.classificationStatus,
  internalManagerOrgUuid: fund.internalManagerOrgUuid,
  managementClassificationNote: fund.managementClassificationNote,
  totalCommitmentAmount: fund.totalCommitmentAmount,
  totalCommitmentSource: fund.totalCommitmentSource,
  totalCommitmentAsOf: fund.totalCommitmentAsOf,
  totalCommitmentAttachmentUuid: fund.totalCommitmentAttachmentUuid,
  ourInvestorOrgUuids: fund.ourInvestorOrgUuids,
  controlRemark: fund.controlRemark,
  fundKind: fund.fundKind,
  registrationStatus: fund.registrationStatus ?? undefined,
  fundType: fund.fundType ?? undefined,
  fundLevel: fund.fundLevel ?? undefined,
  legalRepresentative: fund.legalRepresentative ?? undefined,
  unifiedSocialCreditCode: fund.unifiedSocialCreditCode ?? undefined,
  businessRegistrationDate: fund.businessRegistrationDate ?? undefined,
  businessRegistrationExpiryDate:
    fund.businessRegistrationExpiryDate ?? undefined,
  organizationCode: fund.organizationCode ?? undefined,
  registeredAddress: fund.registeredAddress ?? undefined,
  isSimplifiedManagement:
    (fund.isSimplifiedManagement as FundFormValues['isSimplifiedManagement']) ??
    undefined,
  custodianUuid: fund.custodianUuid ?? undefined,
  fundManagerUuid: fund.fundManagerUuid ?? undefined,
  managerUnifiedSocialCreditCode:
    fund.managerUnifiedSocialCreditCode ?? undefined,
  managementEntity: fund.managementEntity ?? undefined,
  partnershipEntity: fund.partnershipEntity ?? undefined,
  partnershipOrganizationUuid: fund.partnershipOrganizationUuid ?? undefined,
  fundraisingStartAt: fund.fundraisingStartAt ?? undefined,
  fundraisingEndAt: fund.fundraisingEndAt ?? undefined,
  investmentPeriodEndAt: fund.investmentPeriodEndAt ?? undefined,
  exitPeriodEndAt: fund.exitPeriodEndAt ?? undefined,
  strategyType: fund.strategyType ?? undefined,
  registrationNumber: fund.registrationNumber ?? undefined,
  registrationDate: fund.registrationDate ?? undefined,
  investmentSubjectUuid: fund.investmentSubjectUuid ?? undefined,
  investmentSubjectKind: fund.investmentSubjectKind ?? undefined,
  managementPlatformUuid: fund.managementPlatformUuid ?? undefined,
  sourceFundUuid: fund.sourceFundUuid ?? undefined,
  managementFeeRate: fund.managementFeeRate,
  carryRate: fund.carryRate,
  hurdleRate: fund.hurdleRate,
  distributionWaterfall: fund.distributionWaterfall ?? undefined,
  distributionMode: fund.distributionMode ?? undefined,
  distributionBasis: fund.distributionBasis ?? undefined,
  distributionFrequency: fund.distributionFrequency ?? undefined,
  requestReturnRatio: fund.requestReturnRatio,
  requestReturnAmount: fund.requestReturnAmount,
  returnRequirement: fund.returnRequirement ?? undefined,
  returnCompletion: fund.returnCompletion ?? undefined,
  investmentPeriodYears: fund.investmentPeriodYears,
  exitPeriodYears: fund.exitPeriodYears,
  isRollOver: fund.isRollOver ?? undefined,
  isPaybackOver: fund.isPaybackOver ?? undefined,
  investmentPeriodStartAt: fund.investmentPeriodStartAt?.slice(0, 10),
  investmentPeriodExtendEndAt: fund.investmentPeriodExtendEndAt?.slice(0, 10),
  durationDesc: fund.durationDesc ?? undefined,
  investmentManagementFeeBasis: fund.investmentManagementFeeBasis ?? undefined,
  exitManagementFeeBasis: fund.exitManagementFeeBasis ?? undefined,
  exitManagementFeeRate: fund.exitManagementFeeRate,
  extensionManagementFeeType: fund.extensionManagementFeeType ?? undefined,
  managementFeeDesc: fund.managementFeeDesc ?? undefined,
  incomeDistributionDesc: fund.incomeDistributionDesc ?? undefined,
  managerExcessRatio: fund.managerExcessRatio,
  lpExcessRatio: fund.lpExcessRatio,
  custodyFeeDesc: fund.custodyFeeDesc ?? undefined,
  executionServiceFee: fund.executionServiceFee ?? undefined,
  businessScope: fund.businessScope ?? undefined,
  custodyAgreementRequired: fund.custodyAgreementRequired ?? undefined,
  delegatedRepresentative: fund.delegatedRepresentative ?? undefined,
  companyId: fund.companyId ?? undefined,
  companyName: fund.companyName ?? undefined,
  deptId: fund.deptId ?? undefined,
  departmentName: fund.departmentName ?? undefined,
  orgGroupId: fund.orgGroupId ?? undefined,
  groupName: fund.groupName ?? undefined,
});

const nullableTextFields: Array<keyof FundFormValues> = [
  'code',
  'shortName',
  'type',
  'registrationType',
  'organizationForm',
  'managementEntity',
  'partnershipEntity',
  'manager',
  'riskManager',
  'ownerName',
  'managerUnifiedSocialCreditCode',
  'complianceNote',
  'region',
  'strategy',
  'strategyType',
  'controlRemark',
  'fundKind',
  'investmentSubjectKind',
  'investmentRestrictions',
  'registrationNumber',
  'distributionMode',
  'distributionBasis',
  'distributionFrequency',
  'registrationStatus',
  'fundType',
  'fundLevel',
  'legalRepresentative',
  'unifiedSocialCreditCode',
  'organizationCode',
  'registeredAddress',
  'isSimplifiedManagement',
  'returnRequirement',
  'returnCompletion',
  'durationDesc',
  'investmentManagementFeeBasis',
  'exitManagementFeeBasis',
  'extensionManagementFeeType',
  'managementFeeDesc',
  'incomeDistributionDesc',
  'custodyFeeDesc',
  'executionServiceFee',
  'businessScope',
  'isRollOver',
  'isPaybackOver',
  'custodyAgreementRequired',
  'delegatedRepresentative',
  'companyId',
  'companyName',
  'deptId',
  'departmentName',
  'orgGroupId',
  'groupName',
];

const nullableValueFields: Array<keyof FundFormValues> = [
  'ownerUuid',
  'regionUuid',
  'generalPartnerUuid',
  'custodianUuid',
  'fundManagerUuid',
  'investmentSubjectUuid',
  'investmentSubjectKind',
  'managementPlatformUuid',
  'sourceFundUuid',
  'establishedAt',
  'exitPeriodEndAt',
  'fundraisingStartAt',
  'fundraisingEndAt',
  'investmentPeriodEndAt',
  'registrationDate',
  'businessRegistrationDate',
  'businessRegistrationExpiryDate',
  'targetAmount',
  'firstCloseTarget',
  'minimumAmount',
  'gpCommitmentAmount',
  'ourLpCommitmentAmount',
  'finalCloseTarget',
  'ourShareRatio',
  'durationYears',
  'requestReturnRatio',
  'requestReturnAmount',
  'exitManagementFeeRate',
  'managerExcessRatio',
  'lpExcessRatio',
  'investmentPeriodYears',
  'exitPeriodYears',
  'investmentPeriodStartAt',
  'investmentPeriodExtendEndAt',
];

const hasOwn = (value: object, key: PropertyKey) => Object.hasOwn(value, key);

/** Keeps a form clear action as an explicit null instead of a rejected empty string. */
export const normalizeFundFormValues = <T extends Partial<FundFormValues>>(
  values: T,
): T => {
  const next = { ...values } as T;
  if (next.legalFormDetails) {
    const details = Object.fromEntries(
      Object.entries(next.legalFormDetails).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
    if (next.legalFormDetails.basicProfile) {
      const profile = Object.fromEntries(
        Object.entries(next.legalFormDetails.basicProfile).filter(
          ([key, value]) =>
            key !== 'isDistrictFund' && value !== undefined && value !== null,
        ),
      );
      if (Object.keys(profile).length) details.basicProfile = profile;
      else delete details.basicProfile;
    }
    next.legalFormDetails = Object.keys(details).length ? details : null;
  }
  for (const field of nullableTextFields) {
    if (hasOwn(values, field) && typeof next[field] === 'string') {
      (next as Record<string, unknown>)[field] =
        (next[field] as string).trim() || null;
    }
  }
  for (const field of nullableValueFields) {
    if (hasOwn(values, field) && next[field] === undefined) {
      (next as Record<string, unknown>)[field] = null;
    }
  }
  return next;
};

const same = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

/**
 * 档案 DTO 未声明的流程载荷与派生字段不能随档案请求提交。
 * 后端全局 ValidationPipe 开了 forbidNonWhitelisted，多传一个字段就会整单 400。
 */
export const FUND_DTO_UNSUPPORTED_KEYS = new Set<string>([
  'ourRole',
  'ourLpCommitmentAmount',
  'initiationMaterialUuid',
  'managerOverview',
  'investmentEntityId',
  'investmentEntityName',
  'fundPlan',
  'fundSituation',
  'initiationMatterTitle',
  'votingOpinion',
  'managementMode',
  'managementBasis',
  'classificationStatus',
  'firstCloseAt',
  'finalCloseAt',
]);

/** 剥离档案 DTO 未声明字段。 */
export const stripFundDtoUnsupportedFields = <
  T extends Partial<FundFormValues>,
>(
  values: T,
): T =>
  Object.fromEntries(
    Object.entries(values).filter(
      ([key]) => !FUND_DTO_UNSUPPORTED_KEYS.has(key),
    ),
  ) as T;

/** Edit requests contain only real changes so unchanged, frozen terms cannot block profile edits. */
export const buildFundUpdatePayload = (
  values: FundFormValues,
  initial: FundFormValues,
): Partial<FundFormValues> => {
  const current = normalizeFundFormValues(values);
  const baseline = normalizeFundFormValues(initial);
  return Object.fromEntries(
    Object.entries(current).filter(
      ([key, value]) =>
        !FUND_DTO_UNSUPPORTED_KEYS.has(key) &&
        !same(value, baseline[key as keyof FundFormValues]),
    ),
  ) as Partial<FundFormValues>;
};
